/**
 * Agents Page - Read-only view of all agents in the system
 * Shows agent information with tenant, organization, and author details
 */

import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { PageLayout } from '@/layouts/PageLayout';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { DataTable, ColumnDef } from '@/components/shared/DataTable/DataTable';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search, Bot, Loader2 } from 'lucide-react';
import { format } from 'date-fns';

import { useAgents } from '@/hooks/use-agents';
import { useTenants } from '@/features/tenants/hooks/useTenants';
import { useTenantOrganizations } from '@/features/tenants/hooks/useTenantOrganizations';
import { useDebounce } from '@/hooks/use-debounce';

export default function AgentsPage() {
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);
  const [tenantId, setTenantId] = useState<string>('');
  const [organizationId, setOrganizationId] = useState<string>('');
  const limit = 20;

  // Debounce search to avoid too many API calls
  const debouncedSearch = useDebounce(searchTerm, 500);

  // Fetch tenants for filter dropdown
  const { data: tenantsData } = useTenants(1, 100);
  // Fetch organizations only when a tenant is selected
  const { data: orgsData } = useTenantOrganizations(tenantId || undefined);

  const tenants = tenantsData?.tenants || [];
  const organizations = orgsData || [];

  // Fetch agents data with filters
  const { data, isLoading: agentsLoading, error: agentsError } = useAgents({
    page,
    limit,
    search: debouncedSearch || undefined,
    tenant_id: tenantId || undefined,
    organization_id: organizationId || undefined,
  });

  const agents = data?.agents || [];
  const total = data?.total || 0;

  // Calculate stats from current page data
  const stats = {
    total: total,
    withTenant: agents.filter(a => a.tenantName).length,
    withOrg: agents.filter(a => a.organizationName).length,
    totalUsage: agents.reduce((sum, agent) => sum + (agent.usage_count || 0), 0),
  };

  // Reset page when filters change
  const handleSearchChange = (value: string) => {
    setSearchTerm(value);
    setPage(1);
  };

  const handleTenantChange = (value: string) => {
    setTenantId(value === 'all' ? '' : value);
    setOrganizationId(''); // Reset organization when tenant changes
    setPage(1);
  };

  const handleOrganizationChange = (value: string) => {
    setOrganizationId(value === 'all' ? '' : value);
    setPage(1);
  };

  // Define table columns
  const columns: ColumnDef<any>[] = [
    {
      key: 'name',
      header: 'Agent',
      cell: (agent) => (
        <div className="space-y-1">
          <div className="font-medium">{agent.name || 'Unnamed Agent'}</div>
          <div className="text-sm text-muted-foreground line-clamp-2">
            {agent.description || 'No description'}
          </div>
        </div>
      ),
    },
    {
      key: 'authorEmail',
      header: 'Author',
      cell: (agent) => (
        <div className="text-sm">
          {agent.authorEmail ? (
            <span className="font-mono">{agent.authorEmail}</span>
          ) : (
            <span className="text-muted-foreground">Unknown</span>
          )}
        </div>
      ),
    },
    {
      key: 'tenant',
      header: 'Tenant',
      cell: (agent) => (
        <div className="text-sm">
          {agent.tenantName ? (
            <Badge variant="outline">{agent.tenantName}</Badge>
          ) : (
            <span className="text-muted-foreground">No tenant</span>
          )}
        </div>
      ),
    },
    {
      key: 'organization',
      header: 'Organization',
      cell: (agent) => (
        <div className="text-sm">
          {agent.organizationName ? (
            <Badge variant="outline">{agent.organizationName}</Badge>
          ) : (
            <span className="text-muted-foreground">No organization</span>
          )}
        </div>
      ),
    },
    {
      key: 'usage_count',
      header: 'Usage',
      cell: (agent) => (
        <div className="text-sm font-medium">
          {(agent.usage_count || 0).toLocaleString()}
        </div>
      ),
    },
    {
      key: 'created_at',
      header: 'Created',
      cell: (agent) => (
        <div className="text-sm text-muted-foreground">
          {agent.created_at ? format(new Date(agent.created_at), 'MMM d, yyyy') : 'N/A'}
        </div>
      ),
    },
  ];

  if (agentsLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4" />
          <p className="text-muted-foreground">Loading agents...</p>
        </div>
      </div>
    );
  }

  if (agentsError) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive">Failed to load agents</p>
          <p className="text-sm text-muted-foreground mt-2">
            {agentsError.message || 'An error occurred'}
          </p>
        </div>
      </div>
    );
  }

  return (
    <PageLayout
      title="Agents"
      description="View all agents across tenants and organizations"
      icon={<Bot className="h-6 w-6" />}
    >
      {/* Stats Cards */}
      <div className="grid gap-4 md:grid-cols-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Agents</CardTitle>
            <Bot className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.total}</div>
            <p className="text-xs text-muted-foreground">
              All agents in system
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">With Tenant</CardTitle>
            <Bot className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.withTenant}</div>
            <p className="text-xs text-muted-foreground">
              Mapped to tenants
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">With Organization</CardTitle>
            <Bot className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.withOrg}</div>
            <p className="text-xs text-muted-foreground">
              Mapped to organizations
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Usage</CardTitle>
            <Bot className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{stats.totalUsage.toLocaleString()}</div>
            <p className="text-xs text-muted-foreground">
              All time usage count
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Agent List */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Agent List</CardTitle>
              <CardDescription>
                All agents with their tenant, organization, and author information
              </CardDescription>
            </div>
            <div className="flex items-center gap-3">
              <Select value={tenantId || 'all'} onValueChange={handleTenantChange}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="Filter by Tenant" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Tenants</SelectItem>
                  {tenants.map((tenant) => (
                    <SelectItem key={tenant._id} value={tenant._id}>
                      {tenant.displayName || tenant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              {tenantId && (
                <Select value={organizationId || 'all'} onValueChange={handleOrganizationChange}>
                  <SelectTrigger className="w-[180px]">
                    <SelectValue placeholder="Filter by Org" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Organizations</SelectItem>
                    {organizations.map((org) => (
                      <SelectItem key={org._id} value={org._id}>
                        {org.displayName || org.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}

              <div className="relative">
                <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search agents..."
                  value={searchTerm}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-8 w-[250px]"
                />
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <DataTable
            columns={columns}
            data={agents}
            isLoading={agentsLoading}
            emptyMessage="No agents found"
            emptyIcon={<Bot className="h-12 w-12 text-muted-foreground" />}
            pagination={{
              currentPage: page,
              totalItems: total,
              pageSize: limit,
              onPageChange: setPage,
            }}
          />
        </CardContent>
      </Card>
    </PageLayout>
  );
}
